import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import CefrBadge from "../components/CefrBadge";
import ScoreCircle from "../components/ScoreCircle";
import TopBar from "../components/TopBar";
import { CEFR_LABELS, CEFR_ORDER, CEFR_COLORS, CEFR_MUTED, formatDate } from "../lib/cefr";
import { ArrowRight, Sparkle, ShareNetwork, LinkSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const SECTION_LABELS = {
  grammar: "Grammar",
  vocabulary: "Vocab.",
  reading: "Reading",
  listening: "Listening",
  writing: "Writing",
  speaking: "Speaking",
};

// Public share endpoint — no credentials, no interceptors
const publicApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
});

export default function Share() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    publicApi
      .get(`/share/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); });
  }, [slug]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    } catch { toast.error("Could not copy link"); }
  };

  const shareNative = async () => {
    const text = data
      ? `I scored ${Math.round(data.total_score)}/${data.total_max} (${data.cefr_level} — ${CEFR_LABELS[data.cefr_level]}) on the Voxlingua English self-assessment!`
      : "My English level";
    try {
      if (navigator.share) {
        await navigator.share({ title: "My English level", text, url: window.location.href });
      } else {
        await copyLink();
      }
    } catch { /* cancelled */ }
  };

  if (notFound) {
    return (
      <div className="min-h-screen">
        <TopBar />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center neu rounded-3xl p-12 mt-20">
          <h1 className="text-3xl text-foreground font-bold">Result not found</h1>
          <p className="mt-4 text-muted-foreground">
            This link may have expired or is invalid.
          </p>
          <Link
            to="/"
            className="neu-btn-primary inline-flex items-center gap-2 px-6 py-3 mt-6 text-sm"
          >
            Go to homepage <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen">
        <TopBar />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <div className="font-mono-fig text-sm text-muted-foreground">Loading…</div>
        </div>
      </div>
    );
  }

  const cefrIdx = CEFR_ORDER.indexOf(data.cefr_level);
  const displayName = data.first_name || "A learner";

  return (
    <div className="min-h-screen">
      <TopBar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Attribution banner */}
        <div className="neu-sm rounded-full px-5 py-2.5 mb-8 flex items-center gap-2 w-fit mx-auto">
          <Sparkle size={12} weight="fill" className="text-indigo" />
          <span className="text-xs text-muted-foreground">
            Shared result from
          </span>
          <Link to="/" className="text-xs font-bold text-foreground hover:text-indigo">Voxlingua</Link>
        </div>

        {/* Main card */}
        <div className="neu rounded-3xl p-8 md:p-14 text-center">
          <div className="overline">CEFR self-assessment</div>
          <h1 className="text-3xl md:text-5xl mt-4 text-foreground font-bold tracking-tight leading-tight">
            <span className="font-display italic text-indigo font-medium">{displayName}</span>{" "}
            scored
          </h1>

          <div className="mt-10 flex justify-center">
            <ScoreCircle
              score={data.total_score}
              max={data.total_max}
              cefr={data.cefr_level}
              testId="share-score-circle"
            />
          </div>

          <div className="mt-8 flex items-center gap-3 flex-wrap justify-center">
            <CefrBadge level={data.cefr_level} size="lg" testId="share-cefr-badge" />
            <div className="text-lg text-foreground/80">{CEFR_LABELS[data.cefr_level]}</div>
          </div>
          <p className="mt-4 text-foreground/80 leading-relaxed max-w-xl mx-auto">
            {data.cefr_description}
          </p>
          <div className="text-xs text-muted-foreground mt-4">
            Taken on {formatDate(data.created_at)}
          </div>
        </div>

        {/* CEFR ladder */}
        <div className="neu rounded-3xl p-6 md:p-8 mt-6">
          <div className="overline text-center">Where this sits on the CEFR ladder</div>
          <div className="mt-6 flex items-stretch gap-1 sm:gap-2" data-testid="share-cefr-ladder">
            {CEFR_ORDER.map((lvl, i) => {
              const isMe = i === cefrIdx;
              const active = i <= cefrIdx;
              return (
                <div
                  key={lvl}
                  className={`flex-1 min-w-0 flex flex-col items-center gap-2 p-1.5 sm:p-3 transition-colors ${
                    isMe ? "bg-indigo/10" : ""
                  }`}
                >
                  <div
                    className="w-full h-2 rounded-full"
                    style={{ background: isMe ? CEFR_COLORS[lvl] : active ? "hsl(var(--foreground))" : CEFR_MUTED }}
                  />
                  <div className="font-mono-fig text-sm text-foreground">{lvl}</div>
                  <div className={`hidden sm:block text-[11px] text-center break-words ${isMe ? "text-foreground font-bold" : "text-muted-foreground"}`}>
                    {CEFR_LABELS[lvl]}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Section breakdown */}
        {data.section_scores && (
          <div className="neu rounded-3xl p-6 md:p-8 mt-6">
            <div className="overline">Skill breakdown</div>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="share-section-breakdown">
              {Object.keys(SECTION_LABELS).map((s) => {
                const score = data.section_scores?.[s] || 0;
                const max = data.section_max?.[s] || 20;
                const pct = Math.round((score / max) * 100);
                return (
                  <div key={s} className="rounded-2xl p-3 bg-foreground/[0.04]">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{SECTION_LABELS[s]}</div>
                    <div className="font-mono-fig text-xl text-foreground mt-1">{Math.round(score)}</div>
                    <div className="text-[10px] text-muted-foreground">of {max}</div>
                    <div className="mt-2 h-1.5 rounded-full bg-foreground/10 overflow-hidden">
                      <div className="h-full rounded-full bg-indigo" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Share actions */}
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <button
            onClick={shareNative}
            data-testid="share-share-btn"
            className="neu-btn inline-flex items-center gap-2 px-5 py-2.5 text-sm"
          >
            <ShareNetwork size={14} weight="bold" /> Share
          </button>
          <button
            onClick={copyLink}
            data-testid="share-copy-btn"
            className="neu-btn inline-flex items-center gap-2 px-5 py-2.5 text-sm"
          >
            <LinkSimple size={14} weight="bold" /> Copy link
          </button>
        </div>

        {/* Growth CTA */}
        <div className="neu rounded-3xl p-8 md:p-12 mt-10 text-center relative overflow-hidden">
          <div className="overline flex items-center gap-2 justify-center">
            <CheckCircle size={12} weight="duotone" className="text-indigo" /> Curious about your level?
          </div>
          <h2 className="text-3xl md:text-4xl mt-4 text-foreground font-bold tracking-tight leading-tight">
            Take the same test —{" "}
            <span className="font-display italic text-indigo font-medium">it&apos;s free</span>.
          </h2>
          <p className="mt-4 text-muted-foreground max-w-lg mx-auto">
            87 questions across grammar, vocabulary, reading, listening and writing, plus a speaking task.
            Instant CEFR result in about 80 minutes.
          </p>
          <Link
            to="/register"
            data-testid="share-cta-register"
            className="neu-btn-primary inline-flex items-center gap-2 px-7 py-3.5 mt-8 font-medium"
          >
            Take the free test <ArrowRight size={16} weight="bold" />
          </Link>
        </div>

        <div className="text-center text-xs text-muted-foreground mt-10">
          © {new Date().getFullYear()} Voxlingua · A CEFR-aligned self-assessment
        </div>
      </div>
    </div>
  );
}
